import {produce} from 'immer';
import {SearchPageAction, SearchPageState, SearchPageTypeState} from './search-types';
import {ActiveDrawerContent, ProdDocument, SearchSortType, SearchSourceType, TabType} from '../search-types';
import {emptyReduxIndexedNormalizedState} from '../../app-types';
import {createInitialSearchState} from '../search-config';

export const createInitialSearchPageState = (): SearchPageTypeState => ({
    historyEntries: [],
    savedSearches: undefined,
    savedSearchesSharedByOthers: undefined,
    showScrollButton: false,
    showFullDocument: false,
    expandedProdDocument: undefined,
    docs: emptyReduxIndexedNormalizedState(),
    query: '',
    inDocumentQuery: undefined,
    queryPending: false,
    currentSortType: SearchSortType.RELEVANCE,
    docOffset: 0,
    pointInTime: undefined,
    queryFields: undefined,
    spellCheckResult: '',
    resultFor: '',
    sources: [
        {
            name: SearchSourceType.ALL_DOCUMENTS,
            active: true,
        },
        {
            name: SearchSourceType.BOOKMARKS,
            active: false,
        },
    ],
    totalFound: null,
    lastSearchedQuery: '',
    documentDetailsExpanded: true,
    highlightId: undefined,
    navigationRequest: 0,
    documentQueryHighlights: [],
    documentSelectedTags: [],
    previewDocument: undefined,
    documentHighlights: undefined,
    showAnnotations: false,
    resultDetailsTab: {
        openResultTab: TabType.CONTENT,
        size: {x: 500, y: undefined}
    },
    currentPageNumber: 1,
    pageRotations: [0],
    zoomFactor: 1,
    scrollTarget: null,
    resetTransform: false,
    numberOfPages: 1,
    sortTypes: undefined,
    defaultSortType: undefined,
    isDocumentLoading: false,
    isInDocumentSearchPending: false,
    isPreviewHeldForHits: false,
    useAutoCorrection: true,
    activeDrawerContent: ActiveDrawerContent.PLACEHOLDER,
    expandedSpotlightGroups: {}
});

export function searchReducer(
    state: SearchPageState = createInitialSearchState(),
    action: SearchPageAction,
): SearchPageState {
    return produce(state, (draft: SearchPageState) => {
        const searchPageType = (action as SearchPageAction).searchPageType;
        const searchPageState = draft[searchPageType];
        switch (action.type) {
            case 'SET_SEARCH_QUERY_FIELDS': {
                searchPageState.queryFields = action.queryFields;
                break;
            }
            case 'SEARCH_HISTORY_LOADED': {
                searchPageState.historyEntries = action.entries || [];
                break;
            }
            case 'SEARCH_SCROLL_BUTTON_VISIBILITY_CHANGED': {
                searchPageState.showScrollButton = action.showScrollButton;
                break;
            }
            case 'SEARCH_RESULT_SET_EXPANDED_DOCUMENT': {
                if (action.doc) {
                    searchPageState.activeDrawerContent = ActiveDrawerContent.PREVIEW;
                } else if (searchPageState.activeDrawerContent !== ActiveDrawerContent.ASSISTANT) {
                    searchPageState.activeDrawerContent = ActiveDrawerContent.PLACEHOLDER;
                }
                searchPageState.expandedProdDocument = action.doc as ProdDocument;
                break;
            }
            case 'SEARCH_COMPLETED': {
                const responseDocs = action.searchResult.docs as ProdDocument[];

                if (action.searchResult.shouldReplaceSearchResults) {
                    //not executed
                    searchPageState.docs = emptyReduxIndexedNormalizedState();
                }

                addDocs(searchPageState, responseDocs);

                searchPageState.totalFound = action.searchResult.totalFound;
                searchPageState.docOffset = action.searchResult.nextDocOffset as number;
                searchPageState.pointInTime = action.searchResult.pointInTime;
                searchPageState.spellCheckResult = action.searchResult.spellCheckResult as string;
                searchPageState.resultFor = action.searchResult.resultFor as string;
                searchPageState.lastSearchedQuery = action.searchResult.lastSearchedQuery as string;
                break;
            }
            case 'CLEAR_POINT_IN_TIME':
                searchPageState.pointInTime = undefined;
                break;
            case 'FETCH_NEXT_PAGE_COMPLETED':
                addDocs(searchPageState, action.docs);
                searchPageState.docOffset = searchPageState.docOffset + action.docs.length;
                if (action.pointInTime !== undefined) {
                    searchPageState.pointInTime = action.pointInTime;
                }
                break;
            case 'RESET_SEARCH_STATE_PARTS':
                searchPageState.showScrollButton = false;
                searchPageState.docOffset = 0;
                searchPageState.pointInTime = undefined;
                searchPageState.docs = emptyReduxIndexedNormalizedState();
                break;

            // SEARCH
            case 'SEARCH_STARTED':
                searchPageState.queryPending = true;
                searchPageState.query = action.query?.trim() || '';
                searchPageState.showScrollButton = false;
                searchPageState.docOffset = 0;
                searchPageState.pointInTime = undefined;
                searchPageState.spellCheckResult = '';
                searchPageState.resultFor = '';
                searchPageState.docs = emptyReduxIndexedNormalizedState();
                searchPageState.documentDetailsExpanded = true;
                break;
            case 'SEARCH_CHANGE_QUERY':
                searchPageState.query = action.query;
                break;
            case 'FETCH_NEXT_PAGE_STARTED':
                searchPageState.queryPending = true;
                break;
            case 'DONE_SEARCHING_ACTION':
                searchPageState.queryPending = false;
                break;
            case 'SEARCH_SOURCE_SWITCHED': {
                const name = action.name;
                searchPageState.sources = searchPageState.sources.map((source) => {
                    return {
                        ...source,
                        active: source.name === name,
                    };
                });
                break;
            }
            case 'RESULT_SORT_CHANGED':
                searchPageState.currentSortType = action.sortType;
                break;
            case 'SEARCH_ADD_BOOKMARK': {
                const bookmarkDoc: ProdDocument | undefined = searchPageState.docs.byKey[action.doc.id];
                if (bookmarkDoc) {
                    bookmarkDoc.metadata.bookmarked = true;
                }
                break;
            }
            case 'SEARCH_REMOVE_BOOKMARK': {
                const bookmarkDoc: ProdDocument | undefined = searchPageState.docs.byKey[action.doc.id];
                if (bookmarkDoc) {
                    bookmarkDoc.metadata.bookmarked = false;
                }
                break;
            }
            case 'SEARCH_SET_SAVED_SEARCH_HISTORY': {
                searchPageState.savedSearches = action.savedSearches;
                searchPageState.savedSearchesSharedByOthers = action.sharedByOthers;
                break;
            }
            case 'EXPAND_DOCUMENT_DETAILS': {
                searchPageState.documentDetailsExpanded = action.expanded;
                break;
            }
            case 'SWITCH_RESULT_DETAILS_TAB': {
                searchPageState.resultDetailsTab.openResultTab = action.openResultTab;
                break;
            }
            case 'CHANGE_RESULT_DETAILS_TAB_SIZE': {
                searchPageState.resultDetailsTab.size = action.size;
                break;
            }
            case 'SCROLL_TARGET_CHANGED': {
                searchPageState.currentPageNumber = action.newPageNumber ? action.newPageNumber : 1;
                searchPageState.scrollTarget = action.scrollTarget;
                // No `navigate` flag here, unlike SELECTED_HIGHLIGHT_ID. Every dispatcher is a TOC or
                // section click, so setting a target is always a user navigation. Guarded on a non-null
                // target so a future clearing dispatch cannot bump the counter.
                if (action.scrollTarget) {
                    searchPageState.navigationRequest = (searchPageState.navigationRequest ?? 0) + 1;
                }
                break;
            }
            case 'RESET_TRANSFORM': {
                searchPageState.resetTransform = action.resetTransform;
                break;
            }
            case 'SELECTED_DOCUMENT_PAGE_CHANGED': {
                searchPageState.currentPageNumber = action.newPageNumber ? action.newPageNumber : 1;
                searchPageState.scrollTarget = null;
                break;
            }
            case 'SELECTED_DOCUMENT_CHANGED': {
                searchPageState.numberOfPages = action.numberOfPages;
                searchPageState.scrollTarget = null;
                break;
            }
            case 'CURRENT_PAGE_ROTATED': {
                searchPageState.pageRotations[searchPageState.currentPageNumber - 1] = action.rotation;
                break;
            }
            case 'CURRENT_DOCUMENT_ROTATIONS': {
                searchPageState.pageRotations = action.rotations;
                break;
            }
            case 'PREVIEW_DOCUMENT': {
                searchPageState.previewDocument = action.previewDocument;
                break;
            }
            case 'SHOW_ANNOTATIONS': {
                searchPageState.showAnnotations = action.showAnnotations;
                break;
            }
            case 'SEARCH_CHANGE_IN_DOCUMENT_QUERY':
                searchPageState.inDocumentQuery = action.inDocumentQuery;
                break;
            case 'SET_DOCUMENT_HIGHLIGHTS': {
                searchPageState.documentHighlights = action.documentHighlights;
                break;
            }
            case 'SET_DOCUMENT_QUERY_HIGHLIGHTS': {
                searchPageState.documentQueryHighlights = action.documentAnnotations;
                break;
            }
            case 'SET_DOCUMENT_SELECTED_TAGS': {
                searchPageState.documentSelectedTags = action.documentSelectedTags;
                break;
            }
            case 'SELECTED_HIGHLIGHT_ID': {
                searchPageState.highlightId = action.highlightId;
                if (action.navigate) {
                    // `?? 0` for hand-built state (test fixtures, devtools snapshots) predating this
                    // field. `undefined++` is NaN, and immer treats NaN as unchanged, so it would latch.
                    searchPageState.navigationRequest = (searchPageState.navigationRequest ?? 0) + 1;
                }
                break;
            }
            case 'SET_SORT_SETTINGS': {
                searchPageState.sortTypes = action.sortTypes;
                searchPageState.defaultSortType = action.sortTypes?.[0] || {
                    // IN-1803: Changed default sort type from RANDOM to RELEVANCE
                    field: SearchSortType.RELEVANCE.field,
                    order: SearchSortType.RELEVANCE.order
                };
                break;
            }
            case 'IS_LOADING_DOCUMENT': {
                searchPageState.isDocumentLoading = action.isLoading;
                break;
            }
            case 'SET_IN_DOCUMENT_SEARCH_PENDING': {
                searchPageState.isInDocumentSearchPending = action.isPending;
                break;
            }
            case 'SET_PREVIEW_HELD_FOR_HITS': {
                searchPageState.isPreviewHeldForHits = action.isHeld;
                break;
            }
            case 'UPDATE_AUTO_CORRECTION': {
                searchPageState.useAutoCorrection = action.useAutoCorrection;
                break;
            }
            case 'UPDATE_EXPANDED_SPOTLIGHT_GROUPS': {
                const groupId = action.spotlightGroup;
                searchPageState.expandedSpotlightGroups[groupId] = action.expanded;
                break;
            }
            case 'SEARCH_CHAT_TRIGGERED': {
                searchPageState.activeDrawerContent = action.isChatOpen
                    ? ActiveDrawerContent.ASSISTANT
                    : ActiveDrawerContent.PLACEHOLDER;
                searchPageState.expandedProdDocument = undefined;
                break;
            }
        }
    });
}

// TODO: make generalized util method for handling all ReduxNormalizedState or ReduxIndexedNormalizedState?
function addDocs(searchPageState: SearchPageTypeState, responseDocs: ProdDocument[]) {
    // when appending new documents (in case of load more while scrolling),
    // the index should always continue from the last document's index
    const indexOffset = searchPageState.docs.allKeys.length;
    for (let i = 0; i < responseDocs.length; i++) {
        const currentDoc = responseDocs[i];

        searchPageState.docs.byKey[currentDoc.id] = currentDoc; //
        searchPageState.docs.allKeys.push(currentDoc.id);
        searchPageState.docs.docsLengthIncludingDuplicates +=
            1 + (currentDoc.duplicates ? currentDoc.duplicates.length : 0); // the document + duplicates
        searchPageState.docs.index[currentDoc.id] = i + indexOffset;
    }
}
